<template>
    <div class="d-flex flex-column gap-3">
        <!-- Header -->
        <div class="d-flex flex-row gap-2">
            <div class="flex-grow-1">
                <Link as="button"
                      :href="$route('orders.index.store', [this.store.id])"
                      :only="['pendingOrders','paginatedOrders','orderTypes']"
                      class="btn bg-white text-dark btn-hover-primary">
                    <i class="fas fa-chevron-circle-left text-primary"/>
                    {{ __('orders.orders_reservations') }}
                </Link>
            </div>
            <div v-if="loadedOrder.deadline_at" class="pb-1">
                <OrderDeadlineCountdown class="h-100 text-lg" :order="loadedOrder">
                    {{ __('general.remaining_time') }}:
                </OrderDeadlineCountdown>
            </div>
            <OrderTypeBadge class="text-xl h-100 px-5" :type="loadedOrder.type"/>
        </div>

        <!-- Alerts -->
        <transition name="bounce">
            <OrderUnpaidAlert v-if="showPaymentAlert && showOrderTimeline" :store="store" :order="loadedOrder"/>
        </transition>

        <transition name="bounce">
            <WaitForIntegrationAlert v-if="loadedOrder.state === OrderState.WaitForIntegration" :order="loadedOrder"/>
        </transition>

        <!-- Timeline -->
        <OrderTimeline v-if="showOrderTimeline"
                       :order="loadedOrder"
                       :store="store"
                       :users="users"
                       :shipping-providers="shippingProviders"
        />

        <!-- Content -->
        <div class="card">
            <div class="card-header">
                <div class="text-muted text-lg">
                    <div v-if="loadedOrder.integration.code === IntegrationCodeEnum.AmazonTODAY" class="font-weight-bold">
                        {{ __('general.order_nr') }}: {{ loadedOrder.meta?.amazon_order_id }}
                    </div>
                    <div v-else class="font-weight-bold">{{ __('general.order_nr') }}:
                        {{ loadedOrder.integration_order_id }}
                    </div>
                    <div>{{ __('general.channel') }}: {{ loadedOrder.integration.name }}</div>
                    <div>{{ __('general.status') }}: {{ __('orders.states.' + loadedOrder.state) }}</div>
                </div>
                <OrderDetailsTabs v-if="showOrderTimeline"
                                  class="mt-4"
                                  :tab="tab"
                                  :order="loadedOrder"
                                  @switch="toTab => switchTab(toTab)"
                />
            </div>
            <div v-if="showOrderTimeline" class="card-body">
                <OrderItems v-if="tab === 'articles'" :order="loadedOrder" :shipping-cost="shippingCost"/>
                <OrderCustomerDetails v-if="tab === 'address'" :order="loadedOrder"/>
                <OrderShipment v-if="tab === 'shipment'" :order="loadedOrder" :store="store"/>
                <OrderDocuments v-if="tab === 'documents'" :order="loadedOrder" :store="store"/>
                <OrderPayment v-if="tab === 'payment'" :order="loadedOrder"/>
                <OrderRefunds v-if="tab === 'refunds'" :order="loadedOrder" :user-names="users"/>
                <OrderPackages v-if="tab === 'packages'" :order="loadedOrder" :packages="packages"/>
            </div>
            <div v-else class="card-body">
                <div v-if="loadedOrder.refunds.length > 0" class="mb-10">
                    <div class="font-weight-bold font-size-h2 mb-1">
                        Returns
                    </div>
                    <OrderUnprocessedRefunds :store="store" :order="loadedOrder"/>
                </div>

                <div class="font-weight-bold font-size-h2 mb-1">
                    Packages
                </div>
                <OrderPackages :store="store" :order="loadedOrder" :packages="packages"/>
            </div>
        </div>
    </div>
</template>

<script>
import OrderInfoBox from '@components/Orders/OrderInfoBox';
import OrderTypeIcon from '@components/Orders/OrderTypeIcon';
import OrderAddress from '@components/Orders/OrderAddress';
import OrderItems from '@components/Orders/Tabs/OrderItems.vue';
import OrderBadges from '@components/Orders/OrderBadges';
import OrderStateInfo from '@components/Orders/OrderStateInfo';
import OrderActions from '@components/Orders/OrderActions';
import OrderTimeline from '@components/Orders/Timeline/OrderTimeline';
import OrderDocuments from '@components/Orders/Tabs/OrderDocuments.vue';
import OrderRefunds from '@components/Orders/Tabs/OrderRefunds.vue';
import OrderDeadlineCountdown from '@components/Orders/OrderDeadlineCountdown';
import IntegrationIcon from '@components/Integrations/IntegrationIcon';
import Spinner from 'vue-simple-spinner';
import Alert from '@components/Alert.vue';
import StoreImage from '@components/Store/StoreImage.vue';
import Center from '@components/Layout/Center.vue';
import {rewriteUrl} from '@composables/rewriteUrl';
import { Link } from '@inertiajs/vue2';
import StoreLayout from '@layouts/StoreLayout.vue';
import OrderTypeBadge from '@components/Orders/OrderTypeBadge.vue';
import OrderUnpaidAlert from '@components/Orders/Alerts/OrderUnpaidAlert.vue';
import WaitForIntegrationAlert from '@components/Orders/Alerts/WaitForIntegrationAlert.vue';
import OrderShipment from '@components/Orders/Tabs/OrderShipment.vue';
import OrderPayment from '@components/Orders/Tabs/OrderPayment.vue';
import OrderCustomerDetails from '@components/Orders/Tabs/OrderCustomerDetails.vue';
import OrderDetailsTabs from './Includes/OrderDetailsTabs.vue';
import OrderPackageForm from "@components/Orders/OrderPackageForm.vue";
import OrderPackages from "@components/Orders/Tabs/OrderPackages.vue";
import OrderUnprocessedRefunds from "@components/Orders/Refunds/OrderUnprocessedRefunds.vue";
import {OrderState} from "@generated/orderState";
import {IntegrationCodeEnum} from "@generated/integrationCodeEnum";

export default {
    layout: StoreLayout,

    components: {
        OrderUnprocessedRefunds,
        OrderPackages,
        OrderPackageForm,
        OrderDetailsTabs,
        OrderCustomerDetails,
        OrderPayment,
        OrderShipment,
        WaitForIntegrationAlert,
        OrderUnpaidAlert,
        OrderTypeBadge,
        OrderStateInfo,
        OrderInfoBox,
        OrderAddress,
        OrderItems,
        OrderBadges,
        Spinner,
        OrderActions,
        OrderTypeIcon,
        OrderTimeline,
        OrderDocuments,
        OrderRefunds,
        OrderDeadlineCountdown,
        IntegrationIcon,
        Alert,
        StoreImage,
        Center,
        Link,
    },

    props: {
        order: Object,
        store: Object,
        packages: Array,
        shippingProviders: Array,
        shippingCost: Number,
        users: Object,
        tabInit: {
            type: String,
            default: 'articles',
        },
    },

    data() {
        return {
            loadedOrder: this.order,
            tab: 'articles'
        };
    },

    mounted() {
        window.Echo.private('store-orders.' + this.store.id).listen('.order.updated', this.handleUpdatedOrder);
        this.tab = this.tabInit;
    },

    methods: {
        handleUpdatedOrder(updatedOrder) {
            if (updatedOrder.code !== this.order.code) {
                return; //Ignore other order
            }

            this.$set(this, 'loadedOrder', updatedOrder);
        },

        switchTab(tab) {
            this.tab = tab;
            rewriteUrl('tab', tab);
        },
    },

    computed: {
        IntegrationCodeEnum() {
            return IntegrationCodeEnum
        },

        OrderState() {
            return OrderState;
        },

        showOrderTimeline() {
            return this.order.integration.code !== IntegrationCodeEnum.AmazonTODAY
        },

        showPaymentAlert() {
            if ([
                OrderState.Rejected,
                OrderState.Refunded,
                OrderState.Cancelled,
                OrderState.PickedUp,
                OrderState.Pending,
                OrderState.Delivered
            ].includes(this.order.state)
            ) {
                return false;
            }

            return !this.order.is_paid;
        },
    },
};
</script>

<style>
.bounce-enter-active {
    animation: bounce-in 0.5s;
}

.bounce-leave-active {
    transition: all 0.3s ease-out;
}

.bounce-leave-to {
    transform: translateX(20px);
    opacity: 0;
}

@keyframes bounce-in {
    0% {
        transform: scale(0);
    }
    40% {
        transform: scale(1.05);
    }
    100% {
        transform: scale(1);
    }
}
</style>
